import * as THREE from 'three';
import { createLedAdvertising } from '../assets/LedAdvertising';
import type { LedSettings } from '../assets/LedSettings';

export class LedPreview {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10);
  private readonly geometry = new THREE.PlaneGeometry(1, 1);
  private readonly advertising;
  private readonly observer: IntersectionObserver;
  private readonly started = performance.now();
  private frame = 0;
  private renderedAt = -Infinity;
  private visible = false;

  constructor(canvas: HTMLCanvasElement, private settings: Readonly<LedSettings>) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, depth: false, stencil: false });
    this.renderer.setSize(640, 140, false);
    this.advertising = createLedAdvertising(() => this.settings);
    const uv = this.geometry.getAttribute('uv');
    for (let i = 0; i < uv.count; i += 1) uv.setX(i, uv.getX(i) / 7);
    this.scene.add(new THREE.Mesh(this.geometry, this.advertising.material));
    this.camera.position.z = 1;
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
    this.observer.observe(canvas);
    this.frame = requestAnimationFrame(this.tick);
  }

  configure(settings: Readonly<LedSettings>): void { this.settings = settings; }

  private readonly tick = (now: number): void => {
    if (this.visible && !document.hidden && now - this.renderedAt >= 1000 / 15) {
      this.advertising.update((now - this.started) / 1000);
      this.renderer.render(this.scene, this.camera);
      this.renderedAt = now;
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.observer.disconnect();
    this.geometry.dispose();
    this.advertising.material.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
